// filepath: frontend/src/hooks/useRealtimeSync.ts
// Listens to Socket.io org-room events and invalidates TanStack Query cache.
// Plug into AppShell once; all open tabs get live updates without polling.
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSocket } from '@/lib/websocket';
import { useAuthStore } from '@/store/authStore';

export function useRealtimeSync() {
  const { accessToken } = useAuthStore();
  const qc = useQueryClient();

  useEffect(() => {
    if (!accessToken) return;

    const socket = getSocket(accessToken);

    // ── Shipment mutations ─────────────────────────────────────────────────────
    const onShipmentUpdated = (data: { shipment_id?: string }) => {
      qc.invalidateQueries({ queryKey: ['shipments'] });
      qc.invalidateQueries({ queryKey: ['risk-signals'] });
      qc.invalidateQueries({ queryKey: ['observability'] });
      qc.invalidateQueries({ queryKey: ['wip-status'] });
      if (data?.shipment_id) {
        qc.invalidateQueries({ queryKey: ['shipment', data.shipment_id] });
        qc.invalidateQueries({ queryKey: ['shipment-events', data.shipment_id] });
      }
    };

    // Covers both the direct `shipment:updated` and the event-bus `event:shipment.status_changed`
    socket.on('shipment:updated', onShipmentUpdated);
    socket.on('event:shipment.status_changed', (d: any) =>
      onShipmentUpdated({ shipment_id: d?.data?.shipmentId })
    );
    socket.on('event:shipment.created', () => {
      qc.invalidateQueries({ queryKey: ['shipments'] });
      qc.invalidateQueries({ queryKey: ['wip-status'] });
    });

    // ── Signals ────────────────────────────────────────────────────────────────
    const onSignalChange = (d: any) => {
      qc.invalidateQueries({ queryKey: ['action-points'] });
      qc.invalidateQueries({ queryKey: ['email-messages'] });
      qc.invalidateQueries({ queryKey: ['signal-attached-ids'] });
      if (d?.shipment_id) {
        qc.invalidateQueries({ queryKey: ['signals', d.shipment_id] });
        qc.invalidateQueries({ queryKey: ['shipment-events', d.shipment_id] });
      }
    };
    socket.on('signal:created', onSignalChange);
    socket.on('signal:updated', onSignalChange);

    // ── Tasks ──────────────────────────────────────────────────────────────────
    const onTaskChange = (d: any) => {
      if (d?.shipment_id) {
        qc.invalidateQueries({ queryKey: ['tasks', d.shipment_id] });
        qc.invalidateQueries({ queryKey: ['shipment-events', d.shipment_id] });
      }
      qc.invalidateQueries({ queryKey: ['action-points'] });
    };
    socket.on('task:created', onTaskChange);
    socket.on('task:updated', onTaskChange);

    // ── Documents ──────────────────────────────────────────────────────────────
    socket.on('document:added', (d: any) => {
      if (d?.shipment_id) {
        qc.invalidateQueries({ queryKey: ['shipment', d.shipment_id] });
        qc.invalidateQueries({ queryKey: ['shipment-events', d.shipment_id] });
      }
    });

    // ── Invoices ───────────────────────────────────────────────────────────────
    socket.on('invoice:generated', () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['action-points'] });
    });

    // ── Costing & charges ──────────────────────────────────────────────────────
    socket.on('costing:updated', (d: any) => {
      if (d?.shipment_id) qc.invalidateQueries({ queryKey: ['costing', d.shipment_id] });
    });
    socket.on('charges:updated', (d: any) => {
      if (d?.shipment_id) qc.invalidateQueries({ queryKey: ['charges', d.shipment_id] });
    });

    // ── AI insights ────────────────────────────────────────────────────────────
    socket.on('ai:insights', (d: any) => {
      if (d?.shipment_id) qc.invalidateQueries({ queryKey: ['ai-insights', d.shipment_id] });
      qc.invalidateQueries({ queryKey: ['ai-briefing'] });
    });

    return () => {
      socket.off('shipment:updated', onShipmentUpdated);
      socket.off('event:shipment.status_changed');
      socket.off('event:shipment.created');
      socket.off('signal:created', onSignalChange);
      socket.off('signal:updated', onSignalChange);
      socket.off('task:created', onTaskChange);
      socket.off('task:updated', onTaskChange);
      socket.off('document:added');
      socket.off('invoice:generated');
      socket.off('costing:updated');
      socket.off('charges:updated');
      socket.off('ai:insights');
    };
  }, [accessToken, qc]);
}
